import { makeStyles } from '@material-ui/core/styles';

const LIST_WIDTH = 340;
const FAB_SIZE = 56;

const SIDEBAR_GUTTER = 55;

export const useStyles = makeStyles((theme) => ({
    page: {
        position: 'relative',
        display: 'flex',
        width: '100%',
        flex: '1 1 auto',
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'hidden',
    },
    list: {
        width: LIST_WIDTH,
        minWidth: LIST_WIDTH,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        minHeight: 0,
    },
    listWithFab: {
        paddingBottom: theme.spacing(10),
    },
    listNarrowHidden: {
        display: 'none',
    },
    thread: {
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
    },
    threadNarrowFull: {
        width: '100%',
        height: '100%',
    },
    empty: {
        flex: '1 1 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(2),
        textAlign: 'center',
    },
    chatItemSelected: {
        backgroundColor: theme.palette.action.selected,
    },
    chatPreview: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: theme.palette.text.secondary,
    },
    chatPreviewClip: {
        flex: '0 0 auto',
        opacity: 0.7,
    },
    threadHeader: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: 48,
    },
    backButton: {
        marginRight: theme.spacing(1),
    },
    messages: {
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        padding: theme.spacing(1, 2),
        display: 'flex',
        flexDirection: 'column',
        WebkitOverflowScrolling: 'touch',
    },
    bubble: {
        maxWidth: '75%',
        padding: theme.spacing(0.75, 1.5),
        marginBottom: theme.spacing(0.75),
        borderRadius: 12,
        wordBreak: 'break-word',
    },
    bubbleOwn: {
        alignSelf: 'flex-end',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    bubbleIncoming: {
        alignSelf: 'flex-start',
        backgroundColor: theme.palette.type === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
        color: theme.palette.text.primary,
    },
    bubbleAuthor: {
        fontSize: '0.75rem',
        fontWeight: 600,
        opacity: 0.8,
        marginBottom: 2,
    },
    bubbleEmpty: {
        fontStyle: 'italic',
        opacity: 0.6,
    },
    bubbleMeta: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 4,
        fontSize: '0.7rem',
        opacity: 0.7,
        marginTop: 2,
    },
    statusIcon: {
        fontSize: '0.95rem',
        // Лёгкая «вписанность» галочек в пузырь (полупрозрачный контраст).
        opacity: 0.85,
    },
    statusRead: {
        // Прочитано: акцентная галочка, заметно отличается от «доставлено».
        color: theme.palette.type === 'light' ? '#34B7F1' : '#7FD3FF',
        opacity: 1,
    },
    statusFailed: {
        // Не отправлено: акцент ошибки, чтобы пользователь сразу увидел сбой.
        color: theme.palette.error.main,
        opacity: 1,
    },
    composer: {
        display: 'flex',
        alignItems: 'flex-end',
        flexShrink: 0,
        gap: theme.spacing(1),
        padding: theme.spacing(1, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    composerDock: {
        position: 'fixed',
        left: SIDEBAR_GUTTER,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.appBar - 2,
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[4],
    },
    pendingAttachmentsDock: {
        borderTop: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    composerInput: {
        flex: '1 1 auto',
    },
    bubbleAttachments: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
    },
    attachmentImage: {
        maxWidth: 180,
        maxHeight: 180,
        borderRadius: 8,
        cursor: 'pointer',
        objectFit: 'cover',
        display: 'block',
        transition: 'filter 0.15s ease',
        '&:hover': {
            filter: 'brightness(0.92)',
        },
    },
    lightbox: {
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 10,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        cursor: 'zoom-out',
    },
    lightboxImage: {
        maxWidth: '95vw',
        maxHeight: '95vh',
        objectFit: 'contain',
        borderRadius: 4,
        boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5)',
        cursor: 'default',
    },
    lightboxActions: {
        position: 'absolute',
        top: theme.spacing(2),
        right: theme.spacing(2),
        display: 'flex',
        gap: theme.spacing(1),
        zIndex: 1,
    },
    lightboxButton: {
        color: '#fff',
        background: 'rgba(0, 0, 0, 0.4)',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.6)',
        },
    },
    attachmentChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        maxWidth: 220,
        padding: theme.spacing(0.5, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        font: 'inherit',
    },
    attachmentName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    attachmentAudio: {
        display: 'none',
    },
    voicePlayer: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        maxWidth: 260,
        padding: theme.spacing(0.25, 0.75),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
        background: 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        font: 'inherit',
        '& audio': {
            display: 'none',
        },
    },
    voicePlayButton: {
        flexShrink: 0,
        padding: theme.spacing(0.5),
    },
    voiceRecordActive: {
        color: theme.palette.error.main,
        animation: '$voicePulse 1.2s ease-in-out infinite',
    },
    recordingTimer: {
        flexShrink: 0,
        minWidth: 40,
        fontSize: '0.85rem',
        fontVariantNumeric: 'tabular-nums',
        color: theme.palette.error.main,
    },
    '@keyframes voicePulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.45 },
    },
    pendingAttachments: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        flexShrink: 0,
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    pendingChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        maxWidth: 220,
        padding: theme.spacing(0, 0, 0, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 16,
        fontSize: '0.8rem',
    },
    attachmentError: {
        color: theme.palette.error.main,
        fontSize: '0.8rem',
    },
    composeFab: {
        position: 'absolute',
        zIndex: 2,
        bottom: `calc(${theme.spacing(2)}px + var(--messenger-bottom-inset, 0px) + env(safe-area-inset-bottom, 0px))`,
        left: LIST_WIDTH - FAB_SIZE - theme.spacing(2),
    },
    composeFabNarrow: {
        left: 'auto',
        right: theme.spacing(2),
    },
    recipientList: {
        minWidth: 320,
        maxHeight: '50vh',
        overflowY: 'auto',
    },
    composeSubtitle: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        fontSize: 12,
        letterSpacing: 0.5,
        '&:first-child': {
            marginTop: 0,
        },
    },
    composeContent: {
        minWidth: 360,
    },
    composeModeRow: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    composeGroupRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
    },
    composeGroupButton: {
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    composeGroupActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    composeCheckCell: {
        minWidth: 40,
    },
    memberList: {
        maxHeight: '25vh',
        overflowY: 'auto',
    },
    threadTitle: {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));
